import { jwtDecode } from 'jwt-decode';
import { get, isEmpty, isObject, isString } from 'radash';
import { AuthFlow, type AuthState } from 'types';
import { getBaseUrl } from 'utils/baseUrl';

import { getKeycloakConfig, type KeycloakTokenResponse } from './config';
import { mapTokenResponseToAuthState } from './external';
import { refreshKeycloakTokens } from './tokens';

const INTERNAL_LOGIN_SCOPE = 'openid profile email organization:*';

const RESPONSE_DETAIL_PATHS = [
  'errorDetails.detail',
  'errorDetails.Detail',
  'ErrorDetails.detail',
  'ErrorDetails.Detail',
  'detail',
  'Detail',
  'message',
  'Message',
];

export const buildInternalLoginScope = (stayLoggedIn: boolean) => (stayLoggedIn ? `${INTERNAL_LOGIN_SCOPE} offline_access` : INTERNAL_LOGIN_SCOPE);

const hasOrganizationClaim = (accessToken: string | null | undefined): boolean => {
  if (!accessToken) {
    return false;
  }

  try {
    const { organization } = jwtDecode<{ organization?: unknown }>(accessToken);

    return isObject(organization) && !isEmpty(organization);
  } catch {
    return false;
  }
};

const readResponseDetail = (data: unknown): string | undefined => {
  if (isString(data)) {
    return data.trim() || undefined;
  }

  if (!isObject(data)) {
    return undefined;
  }

  const detail = RESPONSE_DETAIL_PATHS.map((path) => get(data, path)).find((value) => isString(value) && value.trim());

  return isString(detail) ? detail : undefined;
};

class KeycloakLoginError extends Error {
  readonly status: number;
  readonly serverMessage?: string;

  constructor(status: number, serverMessage?: string) {
    super('keycloak_login_failed');
    this.name = 'KeycloakLoginError';
    this.status = status;
    this.serverMessage = serverMessage;
  }
}

const mapKeycloakLoginStatus = (status: number, serverMessage?: string): string => {
  switch (status) {
    case 400:
      return serverMessage || 'login.error.exchange';
    case 401:
      return 'login.error.keycloakExpired';
    case 403:
      return 'login.error.keycloakForbidden';
    case 404:
      return 'login.error.keycloakNotFound';
    case 409:
      return 'login.error.keycloakConflict';
    default:
      return 'login.error.exchange';
  }
};

export const toLoginErrorKey = (error: unknown): string => {
  if (error instanceof KeycloakLoginError) {
    return mapKeycloakLoginStatus(error.status, error.serverMessage);
  }

  return 'login.error.exchange';
};

const withBearerPrefix = (accessToken: string) => (accessToken.startsWith('Bearer ') ? accessToken : `Bearer ${accessToken}`);

const loginWithKeycloak = async (accessToken: string, apiKey: string): Promise<{ jwtToken: string }> => {
  const response = await fetch(getBaseUrl('/api/core/Auth/LoginWithKeycloak'), {
    method: 'POST',
    headers: {
      Authorization: withBearerPrefix(accessToken),
      'X-Api-Key': apiKey,
    },
  });

  if (!response.ok) {
    const raw = await response.text();
    let serverMessage: string | undefined;

    if (raw.trim()) {
      try {
        serverMessage = readResponseDetail(JSON.parse(raw));
      } catch {
        serverMessage = raw.trim();
      }
    }

    throw new KeycloakLoginError(response.status, serverMessage);
  }

  const body = (await response.json()) as { jwtToken?: string };

  if (!body.jwtToken) {
    throw new KeycloakLoginError(response.status);
  }

  return { jwtToken: body.jwtToken };
};

export const createAuthStateFromKeycloakTokens = async (tokens: KeycloakTokenResponse, apiKey: string): Promise<AuthState> => {
  if (!hasOrganizationClaim(tokens.access_token)) {
    return mapTokenResponseToAuthState(tokens, apiKey);
  }

  const exchanged = await loginWithKeycloak(tokens.access_token, apiKey);

  return {
    jwtToken: exchanged.jwtToken,
    refreshToken: null,
    apiKey,
    tokenProvider: null,
    idToken: tokens.id_token ?? null,
    authFlow: AuthFlow.Internal,
    keycloakRefreshToken: tokens.refresh_token ?? null,
  };
};

export const exchangeRefreshedInternalSession = async (
  keycloakRefreshToken: string,
  apiKey: string,
  previousIdToken?: string | null
): Promise<AuthState> => {
  const tokens = await refreshKeycloakTokens(keycloakRefreshToken, getKeycloakConfig());
  const exchanged = await loginWithKeycloak(tokens.access_token, apiKey);

  return {
    jwtToken: exchanged.jwtToken,
    refreshToken: null,
    apiKey,
    tokenProvider: null,
    idToken: tokens.id_token ?? previousIdToken ?? null,
    authFlow: AuthFlow.Internal,
    keycloakRefreshToken: tokens.refresh_token ?? keycloakRefreshToken,
  };
};
